import { subHours } from 'date-fns'

import {
  GetRecommendationsQuery,
  GuestMenuItemsByIdsQuery,
  MenuItemCategory,
  RecommendationType,
} from '@/gql/graphql'

import {
  MAX_RECOMMENDED_ITEMS,
  TRENDING_BATCH_SIZE,
  USER_HISTORY_BATCH_SIZE,
} from './constants'

type GuestMenuItemFromQuery = NonNullable<
  GuestMenuItemsByIdsQuery['guestMenuItemsByIds']
>[0]

export interface RecommendedMenuItem extends GuestMenuItemFromQuery {
  types: RecommendationType[]
}

export const filterRecommendedMenuItems = (
  recommendations?: GetRecommendationsQuery['getRecommendations'],
  menuItems?: GuestMenuItemsByIdsQuery['guestMenuItemsByIds'],
): RecommendedMenuItem[] => {
  const items = recommendations?.items
  if (!items?.length || !menuItems?.length) {
    return []
  }

  const allowedCategories = new Set([
    MenuItemCategory.Drink,
    MenuItemCategory.AlcoholicDrink,
    MenuItemCategory.NonAlcoholicDrink,
  ])

  const validItemMap = new Map<string, GuestMenuItemFromQuery>()
  for (const item of menuItems) {
    if (
      item.available &&
      allowedCategories.has(item.category) &&
      item.menuCategories?.length
      //TODO: AI-45 use item.isAvailableForOrdering flag for filtering as well
    ) {
      validItemMap.set(item.id, item)
    }
  }

  // Early return if no valid items
  if (validItemMap.size === 0) {
    return []
  }

  const userHistoryRecs: typeof items = []
  const trendingRecs: typeof items = []

  for (const rec of items) {
    if (rec.types.includes(RecommendationType.UserHistory)) {
      userHistoryRecs.push(rec)
    } else if (rec.types.includes(RecommendationType.Trending)) {
      trendingRecs.push(rec)
    }
  }

  userHistoryRecs.sort((a, b) => {
    const aTime = a.lastOrderedAt ? new Date(a.lastOrderedAt).getTime() : 0
    const bTime = b.lastOrderedAt ? new Date(b.lastOrderedAt).getTime() : 0
    return bTime - aTime
  })

  trendingRecs.sort(
    (a, b) => (b.totalOrderQuantity || 0) - (a.totalOrderQuantity || 0),
  )

  const validateAndTransform = (recs: typeof items): RecommendedMenuItem[] => {
    const result: RecommendedMenuItem[] = []
    for (const rec of recs) {
      const item = validItemMap.get(rec.itemId)
      if (item) {
        result.push({ ...item, types: rec.types })
      }
    }
    return result
  }

  const validatedUserHistory = validateAndTransform(userHistoryRecs)
  const validatedTrending = validateAndTransform(trendingRecs)

  const result: RecommendedMenuItem[] = []
  let userIdx = 0
  let trendingIdx = 0

  while (
    result.length < MAX_RECOMMENDED_ITEMS &&
    (userIdx < validatedUserHistory.length ||
      trendingIdx < validatedTrending.length)
  ) {
    // Add up to 2 user history items
    const userItemsToAdd = Math.min(
      USER_HISTORY_BATCH_SIZE,
      MAX_RECOMMENDED_ITEMS - result.length,
      validatedUserHistory.length - userIdx,
    )
    for (let i = 0; i < userItemsToAdd; i++) {
      result.push(validatedUserHistory[userIdx++])
    }

    // Add up to 2 trending items
    const trendingItemsToAdd = Math.min(
      TRENDING_BATCH_SIZE,
      MAX_RECOMMENDED_ITEMS - result.length,
      validatedTrending.length - trendingIdx,
    )
    for (let i = 0; i < trendingItemsToAdd; i++) {
      result.push(validatedTrending[trendingIdx++])
    }
  }

  return result
}

export const getMenuItemNavigation = (
  menuItem: Pick<RecommendedMenuItem, 'slug' | 'menuCategories'>,
) => {
  const itemCategory = menuItem.menuCategories?.[0]
  const itemSection = itemCategory?.menuSections.find((section) =>
    section.menuItems?.some((item) => item.slug === menuItem.slug),
  )

  return {
    category: itemCategory,
    section: itemSection,
  }
}

export const hasOrderInLast24Hours = (
  recommendations?: GetRecommendationsQuery['getRecommendations'],
): boolean => {
  if (!recommendations?.items?.length) {
    return true
  }

  const now = new Date()
  const twentyFourHoursAgo = subHours(now, 24)

  const hasOrderWithinRange = recommendations.items.some((rec) => {
    // Only check UserHistory recommendations as they contain actual order history
    if (
      !rec.types.includes(RecommendationType.UserHistory) ||
      !rec.lastOrderedAt
    ) {
      return false
    }

    const lastOrderDate = new Date(rec.lastOrderedAt)
    // Only consider past orders within the last 24 hours (ignore future timestamps)
    return lastOrderDate > twentyFourHoursAgo && lastOrderDate <= now
  })

  return !hasOrderWithinRange
}
